import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { ChipModule } from 'primeng/chip';
import { AbstractControl, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { TabViewModule } from 'primeng/tabview';
import { InputTextModule } from 'primeng/inputtext';
import { FloatLabelModule } from 'primeng/floatlabel';
import { RadioButtonModule } from 'primeng/radiobutton';
import { InputSwitchModule } from 'primeng/inputswitch';
import { MultiSelectModule } from 'primeng/multiselect';
import { CheckboxModule } from 'primeng/checkbox';
import { CommonModule } from '@angular/common';
import { DropdownModule } from 'primeng/dropdown';
import { CalendarModule } from 'primeng/calendar';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { Table, TableLazyLoadEvent, TableModule, TableRowCollapseEvent, TableRowExpandEvent } from 'primeng/table';
import { DialogModule } from 'primeng/dialog';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { DocumentService } from '../../../../../../shared/services/document-service';
import { ProjectService } from '../../../../../../shared/services/project.service';
import { ToastService } from '../../../../../../shared/services/toast.service';
import { ConfirmationDialogService } from '../../../../../../shared/services/confirmation-dialog.service';
import { UserClientVm } from '../../../../../../store/user/user.interface';
import { UserService } from '../../../../../../shared/services/user.service';
import { constants } from '../../../../../../shared/constants/constants';
import { ActivatedRoute, Router } from '@angular/router';
import { Firm } from '../../../../../../shared/interfaces/firm.interface';
import { Project, Role } from '../../project.interface';
import { Documents, TemplateFields } from '../../../../../../shared/interfaces/document.interface';
import { ProjectNavigationService } from '../../../../../../shared/services/project-navigation.service';
import { EditTemplateComponent } from './edit-template/edit-template.component';
import { EditTemplateService } from './edit-template/edit-template.service';
import { RoleService } from '../../../../../../shared/services/role.service';
import { TooltipModule } from 'primeng/tooltip';
import { PipesModule } from '../../../../../../shared/pipes/pipes.module';
import { HelperService } from '../../../../../../shared/services/helper.service';

@Component({
  selector: 'app-project-documents',
  standalone: true,
  imports: [
    DragDropModule,
    ChipModule,
    DialogModule,
    TableModule,
    TabViewModule,
    InputIconModule,
    DropdownModule,
    CalendarModule,
    CheckboxModule,
    IconFieldModule,
    InputTextareaModule,
    FormsModule,
    RadioButtonModule,
    FloatLabelModule,
    InputTextModule,
    InputSwitchModule,
    MultiSelectModule,
    ReactiveFormsModule,
    CommonModule,
    EditTemplateComponent,
    TooltipModule,
    PipesModule
  ],
  templateUrl: './documents.component.html',
  styleUrl: './documents.component.scss'
})
export class ProjectDocumentsComponent implements OnInit, OnChanges, OnDestroy {

  @Input()
  project: Project | null = null;
  @Output() 
  updateProjectState: EventEmitter<void> = new EventEmitter<void>();
  @Output()
  changeTab: EventEmitter<number> = new EventEmitter<number>();
  @ViewChild('documentTable') table: Table | undefined;

  selectTemplateType: string = 'Existing';
  checked: boolean = false;
  expandedRows = {};
  documents: any = [];
  totalDocuSignTemplate = 0;
  docuSearchText = '';
  searchOfferDebounce: any = null;
  templateDocuments: any = [];
  user!: UserClientVm;
  visible: boolean = false;
  visibleResumeDialog: boolean = false;
  showAppRole = false;
  pdfSource: any;
  selectedDocuments: any = [];
  selectedDocSignTemplate: any;
  projectId!: string | null;
  projectFiles: any = [];
  fileId: any;
  docTitleRequired = false;
  templateName: any = '';
  docTitle: any;
  pdfDocuments: any = [];
  projectResumeFile: any = null; 
  showResumeDocUploadError = '';
  firm: Firm | null = null;
  fileName = 'No file selected';
  reviewDocuments: Array<Documents> = [];
  projectRoles: Role[] = [];
  resumeDocumentForm!: FormGroup;
  readOnlyDocumentType: 'DOCUMENT' | 'LINK' = 'DOCUMENT';

  isEditResumeDocument = false;
  docuSignEditorOpen = false;
  isEditMode = false;
  selectedResumeDocument: Documents | null = null;
  visibleDocuSignConfirmModal = false;
  visibleMultiDocuSignConfirmModal = false;
  docAdded = false;
  addToTemplate = false;
  permanentAddconfirmVisible = false;
  documentMetadata: any = null; 
  compositeDocuSignTemplateKey = '';

  isSaveTemplateInProgress = false;

  constructor(private route: ActivatedRoute, private projectService: ProjectService, private roleService: RoleService,
    private documentService: DocumentService, private editTemplateService: EditTemplateService, private confirmationDialogService: ConfirmationDialogService, 
    private userService: UserService, private toastService: ToastService, private fb: FormBuilder, 
    private projectNavigation: ProjectNavigationService, private router: Router, public helperService: HelperService) {}

  ngOnInit() { 
    this.projectId = this.route.snapshot.paramMap.get('projectId');
    this.user = this.userService.getSessionUser();
    this.firm = this.user.firm;
    this.initializeForm();
    this.initializeList();
    this.getProjectRoles();
  }

  updateProject(): void{
    this.updateProjectState.emit();
  }

  initializeList(): void{
    this.getProjectInfo();
  }

  ngOnChanges(changes: SimpleChanges) {
    if(this.project){
      this.initializeList();
      this.getDocumentsByIds();
    }
  }

  getProjectInfo(){
    this.compositeDocuSignTemplateKey = (this.project?.docuSignTemplates || ['']).map((doc) => {
      return doc;
    }).join('_');
  }

  getProjectRoles() {
    this.roleService.getProjectRoles(this.projectId!).subscribe((resp: Role[]) => {
      this.projectRoles = resp || [];
    });
  }

  getDocumentsByIds(): void {
    if(this.project && this.project.documents && this.project.documents.length){
      const commaSeparatedDocIds = this.project.documents.filter((doc: any) => !doc.isDeleted).map((doc: any) => doc._id).join(',');
      if(commaSeparatedDocIds){
        this.documentService.getDocumentsByIds(commaSeparatedDocIds).subscribe({
          next: (response) => {
            this.reviewDocuments.length = 0;
            response.forEach((item: Documents) => {
              this.reviewDocuments.push(item);
            });
          }
        });
      } else {
        this.reviewDocuments.length = 0;
      }
    }
  }

  getDocumentAndTemplate(){
    const documentTemplate: Array<any> = this.selectedDocuments.filter((template: any) => template.documents && template.documents.length).map((template: any) => {
      return {templateId: template.templateId, templateName: template.name, documents: template.documents, _id:template._id.toString()}
    });

    return documentTemplate;
  }

  getRadioGroupField(documentIdIndex: number, pageId: any, fields: any, fieldAt: any, documentFields: any ) {
    const selectedRadioObject = fields[fieldAt]; 

    if(!selectedRadioObject) {
      return selectedRadioObject;
    }
    selectedRadioObject.documentId = documentIdIndex+1;
    selectedRadioObject.pageNumber = pageId;
    selectedRadioObject.radioGroupTabs = [];
    
    const selectedRadioGroupValues = Object.values(selectedRadioObject.radioGroup);
    if(selectedRadioObject.top === undefined || selectedRadioObject.left === undefined ) {
      selectedRadioObject.top  = constants.defaultDocuSignFieldsCoordinates['radioTab'].top;
      selectedRadioObject.left  = constants.defaultDocuSignFieldsCoordinates['radioTab'].left;
    }
    selectedRadioGroupValues.map((radio: any, index: number) => {
      const radioTabOption = documentFields[pageId]['radioTab'][radio.id];
      radio.tabName = radioTabOption ? radioTabOption.tabName:'';
      radio.selected = !!radio.selected;
      radio.documentId = documentIdIndex+1;
      radio.pageNumber = pageId;
      if(selectedRadioObject.alignHorizontal) {
        radio.xPosition = selectedRadioObject.left+ ((index+1)*28 -11);
        radio.yPosition = selectedRadioObject.top +15;
      } else {
        radio.xPosition = selectedRadioObject.left;
        radio.yPosition = selectedRadioObject.top + ((index+1)*28 -11);
      }
      selectedRadioObject.radioGroupTabs.push(radio);
    }); 

    delete selectedRadioObject.radioGroup;
    delete selectedRadioObject['$$hashKey'];
    
    return selectedRadioObject;
  }

  getXPositionOfFieldType(fieldValue: any, fieldType: string) {
    if(fieldValue === undefined) {
        const defaultDocuSignFieldsCoordinates: any = constants.defaultDocuSignFieldsCoordinates;
        const defaultFieldValue = defaultDocuSignFieldsCoordinates[fieldType];
        return defaultFieldValue.left;
      }

      return fieldValue;
  }

  getYPositionFieldType(fieldValue: any, fieldType: string) {
      if(fieldValue === undefined) {
        const defaultDocuSignFieldsCoordinates: any = constants.defaultDocuSignFieldsCoordinates;
        const defaultFieldValue = defaultDocuSignFieldsCoordinates[fieldType];
        return defaultFieldValue.top;
      }

      return fieldValue;
  }

  goToPrevious(index: number): void {
    this.changeTab.emit(index);
  }

  async next(requestState: string): Promise<void> {
    if(this.firm?.isDocusignEnabled){
      this.onDocuSignEnableNext(requestState);
    } else {
      this.onDocuSignDisableNext(requestState);
    }
  }

  updateTemplateDocument(): void{
    const templateDocuments = this.editTemplateService.getDocumentInfoByKey('uploadedFiles');
    this.templateDocuments = templateDocuments && templateDocuments.length ? templateDocuments : this.templateDocuments;
  }

  async onDocuSignEnableNext(requestState: string): Promise<void>{
    const pId = this.projectId || '';
    const firmId = this.firm?._id;

    if (this.selectTemplateType === 'New') {
      await this.handleNewTemplateCreation(pId, firmId || '', requestState);
    } else {
      this.handleExistingTemplateSelection(pId, firmId || '', requestState);
    }
  }

  private async handleNewTemplateCreation(pId: string, firmId: string, requestState: string): Promise<void> {
    this.isSaveTemplateInProgress = true;

    if (!this.validateNewTemplateRequirements()) {
      return;
    }

    const templateFields = this.processTemplateFields();
    const { documents, fileOptions } = this.prepareTemplateData(pId);

    this.createProjectTemplate(pId, firmId, documents, fileOptions, templateFields);
  }

  private validateNewTemplateRequirements(): boolean {
    if (!this.templateName) {
      this.toastService.showError('Required fields are missing');
      return false;
    }

    if (this.templateDocuments.length === 0) {
      this.toastService.showError('Please upload a document');
      return false;
    }

    return true;
  }

  private processTemplateFields(): any {
    const templateFields: any = {};
    
    this.templateDocuments.forEach((document: any, index: number) => {
      this.processDocumentFields(document, index, templateFields);
    });

    return templateFields;
  }

  private processDocumentFields(document: any, documentIndex: number, templateFields: any): void {
    for (const page in document.templateFields) {
      const fieldTypes: Array<TemplateFields> = document.templateFields[page];
      this.processPageFieldTypes(fieldTypes, page, documentIndex, document.templateFields, templateFields);
    }
  }

  private processPageFieldTypes(
    fieldTypes: Array<TemplateFields>, 
    page: string, 
    documentIndex: number, 
    documentFields: any, 
    templateFields: any
  ): void {
    for (const fieldType in fieldTypes) {
      if (!Array.isArray(templateFields[fieldType])) {
        templateFields[fieldType] = [];
      }
      
      const fields: any = Object.values(documentFields[page][fieldType]);
      this.processFieldsForType(fields, fieldType, documentIndex, page, documentFields, templateFields);
    }
  }

  private processFieldsForType(
    fields: any[], 
    fieldType: string, 
    documentIndex: number, 
    page: string, 
    documentFields: any, 
    templateFields: any
  ): void {
    for (let fieldI = 0; fieldI < fields.length; fieldI++) {
      if (this.isRadioTabField(fieldType, fields, fieldI)) {
        const radioFieldObject = this.getRadioGroupField(documentIndex, page, fields, fieldI, documentFields);
        templateFields[fieldType].push(radioFieldObject);
      } else if (this.isValidFieldForProcessing(fields[fieldI])) {
        const fieldObject = this.createFieldObject(fields[fieldI], fieldType, documentIndex, page);
        templateFields[fieldType].push(fieldObject);
      }
    }
  }

  private isRadioTabField(fieldType: string, fields: any[], fieldIndex: number): boolean {
    const field = fields[fieldIndex];
    return fieldType === 'radioTab' && fields.length > 0 && field?.domid;
  }

  private isValidFieldForProcessing(field: any): boolean {
    return field && Object.values(field).length > 0;
  }

  private createFieldObject(field: any, fieldType: string, documentIndex: number, page: string): any {
    return {
      xPosition: Math.round(this.getXPositionOfFieldType(field.left, fieldType)),
      yPosition: Math.round(this.getYPositionFieldType(field.top, fieldType)),
      documentId: documentIndex + 1,
      pageNumber: page,
      groupId: field?.groupId,
      tabGroupLabel: field?.tabGroupLabel,
      tabLabel: field.tabLabel,
      groupTooltip: field.groupTooltip,
      validationType: field.validationType,
      validationCount: field.validationCount,
      validationCountMin: field.validationCountMin,
      validationCountMax: field.validationCountMax,
      conditionalFields: field.conditionalFields ? field.conditionalFields.map((condition: any) => ({
        selectedElement: condition.selectedElement,
        checked: condition.checked
      })) : null,
      height: field.height,
      width: field.width,
      editable: field.editable,
      tooltip: field.tabName,
      tabName: field.tabName,
      required: field.required
    };
  }

  private prepareTemplateData(pId: string): { documents: any[], fileOptions: any[] } {
    const documents = this.templateDocuments.map((doc: any) => doc.file);
    const fileOptions = this.templateDocuments.map((doc: any) => ({
      projectId: pId,
      name: doc.name,
      docTitle: doc.docTitle
    }));

    return { documents, fileOptions };
  }

  private createProjectTemplate(
    pId: string, 
    firmId: string, 
    documents: any[], 
    fileOptions: any[], 
    templateFields: any
  ): void {
    this.projectService.createProjectTemplate(
      pId, 
      firmId, 
      documents, 
      fileOptions, 
      this.templateName, 
      this.getTemplateFieldsOrDefault(templateFields)
    ).subscribe({
      next: (response: any) => {
        this.handleTemplateCreationSuccess();
      },
      error: (error: any) => {
        this.onFailureUpsertingTemplate(error);
      }
    });
  }

  private handleTemplateCreationSuccess(): void {
    this.isSaveTemplateInProgress = false;
    this.reloadTableData();
    this.selectTemplateType = 'Existing';
    this.projectFiles = [];
    this.docTitle = '';
    this.templateDocuments = [];
    this.editTemplateService.setDocumentInfoByKey('uploadedFiles', []);
  }

  private handleExistingTemplateSelection(pId: string, firmId: string, requestState: string): void {
    const documentTemplate = this.getDocumentAndTemplate();
    const compositeDocuSignKey = documentTemplate.map(temp => temp._id).join('_');
    const projectStep = Number(this.project?.step || 0);

    if (this.docAdded) {
      this.addTemplateDocumentConfirmation(projectStep, pId, firmId, documentTemplate, requestState);
    } else {
      this.multipleTemplateSelected(compositeDocuSignKey, projectStep, pId, firmId, documentTemplate, requestState);
    }
  }

  removeDocument(index: number){
    const projectFiles: Array<any> = [];
    const template = this.templateDocuments[index];
    this.projectFiles.forEach((projectFile: any) => {
      if(template.file !== projectFile){
        projectFiles.push(projectFile);
      }
    });
    this.projectFiles = projectFiles;
    this.templateDocuments.splice(index, 1);
  }

  addTemplateDocumentConfirmation(step: number, pId: string, firmId: string, documentTemplate: any, requestState: any){
    this.documentMetadata = {
      step, 
      pId, 
      firmId, 
      documentTemplate, 
      requestState
    };
    if(this.projectRoles.length){
      this.visibleDocuSignConfirmModal = true;
    } else {
      this.goToNextState(requestState);
    }
  }

  openDocuSignEditor(docuSignEditorOpen = false): void{
    this.docuSignEditorOpen = docuSignEditorOpen;
  }

  multipleTemplateSelected(compositeDocuSignKey: any, step: number, pId: string, firmId: string, documentTemplate: any, requestState: any){
    this.documentMetadata = {
      compositeDocuSignKey,
      step, 
      pId, 
      firmId, 
      documentTemplate, 
      requestState
    };
    const projectStep = Number(this.project?.step || 0);

    if(compositeDocuSignKey !== this.compositeDocuSignTemplateKey && projectStep === 7 && this.projectRoles.length){
      this.visibleMultiDocuSignConfirmModal = true; 
    } else {
      this.updateProjectTemplate(step, pId, documentTemplate, firmId, requestState);
    }
  }

  setDocReqForStaff(confirm = false){
    this.visibleDocuSignConfirmModal = false;
    const {step, pId, documentTemplate, firmId, requestState} = this.documentMetadata;
    
    if(confirm){
      if(this.addToTemplate){
        this.createAdditionalProjectTemplate(step, pId, documentTemplate, firmId, requestState);
      }
      else{
        this.updateProjectTemplate(step, pId, documentTemplate, firmId, requestState);
      }
      this.toastService.showSuccess("Document(s) have been sent to existing users");
    }
  }

  async createAdditionalProjectTemplate(step: number, pId: string, documentTemplate: any, firmId: string, requestState: any) {
    
    try {
      await this.documentService.addAdditionalProjectTemplateDocuments(pId, firmId, documentTemplate);
      this.onCreatingOrUpdatingTemplateSuccessfully(step, pId, requestState);
      this.projectFiles = [];
      this.docTitle = '';
      this.addToTemplate = false;
    } catch (error) {
      this.onFailureUpsertingTemplate(error);
    }
  }

  async updateProjectTemplate(step: number, pId: string, documentTemplate: any, firmId: string, requestState: any){
    try {
      this.isSaveTemplateInProgress = true;
      await this.documentService.updateProjectDocuSignTemplate(pId, firmId, documentTemplate);
      this.onCreatingOrUpdatingTemplateSuccessfully(step, pId, requestState);
      this.projectFiles = [];
      this.docTitle = '';
      this.isSaveTemplateInProgress = false;
    } catch (error) {
      this.isSaveTemplateInProgress = false;
      this.onFailureUpsertingTemplate(error);
    }
  }

  async onCreatingOrUpdatingTemplateSuccessfully(step: number, pId: string, requestState: string) {
    const projectStep = Number(this.project?.step || 0);
    if (projectStep < step) {
      const response = await this.projectService.updateProjectStep(pId, step)
      if (response.status === 200) {
        if (requestState === "review") {
          this.changeTab.emit(4);
        } else {
          this.changeTab.emit(2);
        }
      }
    } else {
      this.goToNextState(requestState);
    }
  }

  goToNextState(state: string) {
    const requestState = state || 'next';
    if (requestState === 'next') {
        this.changeTab.emit(2);
    } else if (requestState === 'review') {
        this.changeTab.emit(4);
    } else if (requestState === 'save') {
        this.goToProjectListing();
    }
  }
  

  setMultiDocReqForStaff(confirm = false){
    this.visibleMultiDocuSignConfirmModal = false;
    
    const {step, pId, documentTemplate, firmId, requestState} = this.documentMetadata;

    if(confirm){
      try {
        this.updateProjectTemplate(step, pId, documentTemplate, firmId, requestState);
        this.toastService.showSuccess("Document(s) have been sent to existing users");
      } catch (error) {
        this.toastService.showSuccess("Document(s) have been saved and will be sent to new users");
      }
    }
  }

  uploadPermanentDocs(confirm = false){
    this.permanentAddconfirmVisible = false;
    if(confirm){
      this.updateInExistingTemplate({file: this.templateDocuments, title: this.docTitle});
    }
  }

  onFailureUpsertingTemplate(error: any){
    if (error?.data?.message || error?.message) {
        this.toastService.showError((error?.data?.message || error?.message) || "Error while adding credentials.");
    } else if (error?.statusText && error?.statusText === 'Request Entity Too Large') {
        this.toastService.showError('File is too large');
    }
  }

  async onDocuSignDisableNext(requestState: string): Promise<void>{
    const projectStep = Number(this.project?.step || 0);
    const step = 5;
    if (projectStep < step) {
        this.projectNavigation.setCurrentStep(step);
        await this.projectService.updateProjectStep(this.projectId || '', step);
        if (requestState === 'review') {
            this.changeTab.emit(4);
        } else if (requestState === 'save') {
          this.goToProjectListing();
        } else {
          this.changeTab.emit(2);
        }
    } else {
        this.projectNavigation.setCurrentStep(projectStep);
        this.goToNextStep(requestState);
    }
  }

  goToNextStep(requestState: string) {
    if (requestState === 'next') {
      this.changeTab.emit(2);
    }
    else if (requestState === 'review') {
      this.changeTab.emit(4);
    }
    else if (requestState === 'save') {
        this.goToProjectListing();
    }
  }

  reloadTableData(): void{
    if (this.table) {
      const event: TableLazyLoadEvent = {
        first: this.table.first || 1,
        rows: this.table.rows
      };
      this.getDocuSignTemplates(event);
    }
  }

  editTemplateDocumentView(templateId: string, documentId: string, documentName: string, docuSignTemplate: any){
    this.documentService.getDocuSignDocument(templateId, documentId).subscribe({
      next: async (res) => {
        const url = URL.createObjectURL(new Blob([res]));
        const response = await fetch(url);
        const blob = await response.blob();
        const pdfContent = await this.getFileReaderObject(blob);
        
        const docIndex =  docuSignTemplate.documents.findIndex((doc: any) => {
          return doc.name === documentName;
        });
        const documentUri = pdfContent;;
        this.editTemplateService.setDocumentInfoByKey('editTemplate',
          { 
            documentId: docIndex+1, 
            documentUri,
            docuSignTemplate 
          }
        );
        this.isEditMode = true;
        this.docuSignEditorOpen = true;
      },
      error: (error) => {
      }
    })
  }

  viewDocumentForTemplate(document: any) {
    this.editTemplateService.setDocumentInfoByKey('editTemplate', null);
    this.editTemplateService.setDocumentInfoByKey('uploadedFiles', this.templateDocuments);
    this.editTemplateService.setDocumentInfoByKey('file',document.file.length?document.file[0]:document.file);
    this.editTemplateService.setDocumentInfoByKey('templateName', this.templateName);
    this.isEditMode = false;
    this.docuSignEditorOpen = true;
  }

  getFileReaderObject(blob: any): Promise<any>{
    const reader = new FileReader();
    
    return new Promise((resolve, reject) => {
      reader.onloadend = () => {
        resolve(reader.result);
      };
      reader.onerror = () => {
        reject(new Error('Failed to read the blob content'));
      };
      
      reader.readAsText(blob);
    });
  }

  initializeForm(): void{
    if(!this.firm?.isDocusignEnabled){
      this.resumeDocumentForm = this.fb.group({
        docTitle: ['', [Validators.required]],
        instructions: [''],
        url: ['']
      })
    }
  }

  private isValidHttpUrl(url: string): boolean {
    try {
      const parsedUrl = new URL(url);
      return parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:';
    } catch {
      return false;
    }
  }

  private getTrimmedUrl(): string {
    return (this.resumeDocumentForm.get('url')?.value || '').trim();
  }

  get resumeForm(): { [key: string]: AbstractControl } {
    return this.resumeDocumentForm.controls;
  }

  onLazyLoad(event: TableLazyLoadEvent ){
    this.getDocuSignTemplates(event);
  }

  getDocuSignTemplates(event: TableLazyLoadEvent | null = null){
    const offset = event?.first || 0;
    const size = event?.rows || 10;
    this.documentService.getDocuSignTemplates(this.projectId!, this.docuSearchText, size, offset).subscribe({
      next: (documents) => {
        this.documents = documents.envelopeTemplates;
        
        this.totalDocuSignTemplate = documents.total;
        this.selectedDocuments = this.documents.filter((document: any) => document.selected);
        if(this.templateName){
          const document = this.documents.find((doc: any) => doc.name === this.templateName);
          this.templateName = '';
          this.selectedDocuments.push(document);
        }
      }
    });
  }

  searchDocuTemplate(): void{
    if (this.searchOfferDebounce) {
      clearTimeout(this.searchOfferDebounce);
    }
    this.searchOfferDebounce = setTimeout(() => {
      this.getDocuSignTemplates();
    }, 1000);
  }

  ngOnDestroy(): void {
    // The search debounce fires an HTTP-backed template reload. A timer that
    // outlives the component fires a stray request after navigation (and, in
    // tests, hits the destroyed injector with NG0205).
    if (this.searchOfferDebounce) {
      clearTimeout(this.searchOfferDebounce);
    }
  }

  addDocumentInTemplate($event:Event, docSignTemplate: any) {
    if(docSignTemplate.createdBy !== this.user._id){
      this.toastService.showError('You are not authorized to add document in this template');
      return;
    }
    this.selectedDocSignTemplate = docSignTemplate;
    this.showDialog();
  }

  viewDocument(templateId: string, documentId: string, documentName: string) {

    this.documentService.getDocuSignDocument(templateId, documentId).subscribe(async (res) => {
      const url = URL.createObjectURL(new Blob([res]));
      const response = await fetch(url);
      const blob = await response.blob();
      const pdfContent = await this.documentService.getFileReaderObject(blob);

      const uri = `data:application/octet-stream,${window.escape(pdfContent)}` ;
      this.pdfSource = {url: uri};
      let downloadLink = document.createElement("a");
      downloadLink.href = uri;
      downloadLink.download = documentName.indexOf(".pdf")>-1?documentName:documentName+".pdf";
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    });
  }

  deleteTemplateDocument($event:Event, documentId: string, templateId: string, docSignTemplate: any) {
    if(docSignTemplate.createdBy !== this.user._id){
      this.toastService.showError('You are not authorized to delete this template');
      return;
    }
    $event.stopPropagation();
    this.confirmationDialogService.confirm({
      target: $event.target as EventTarget,
      message: 'Are you sure you want to delete this ' + (docSignTemplate.documents.length === 1 ? 'template?': 'document?'),
      header: 'Confirmation',
      icon: 'pi pi-exclamation-triangle',
      acceptIcon:"none",
      rejectIcon:"none",
      rejectButtonStyleClass:"p-button-text",
      accept: () => {
          this.documentService.deleteDocuSignDocument(documentId, templateId, docSignTemplate._id).subscribe((res) => {
          this.toastService.showSuccess(docSignTemplate.documents.length === 1 ? 'Document has been deleted successfully': 'Document' + ' has been deleted');
          this.getDocuSignTemplates();
        });
      }
    });
  }

  async uploadDocument($event: Event) {
    const files = ($event.target as HTMLInputElement)?.files;
    
    if (!files?.length) {
      return;
    }

    const validationResult = await this.validateUploadedFiles(files);
    if (!validationResult) {
      return;
    }

    const isDuplicate = this.checkForDuplicateFiles(files);
    if (!isDuplicate) {
      this.addFilesToProject(files);
    }

    this.addDocumentsToList();
  }

  private async validateUploadedFiles(files: FileList): Promise<boolean> {
    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      if(file.type === 'application/pdf') { 
        const isValid = await this.helperService.validatePdf(file);
        if (!isValid) {
          this.toastService.showError("Corrupted or invalid PDF file.");
          return false;
        }
      }

      if (this.isGreaterThanAllowedSize(file)) {
        this.toastService.showError("Document size is greater than " + constants.documentUploadSizeInMbs + " limit.");
        return false;
      }
    }
    return true;
  }

  private checkForDuplicateFiles(files: FileList): boolean {
    return this.projectFiles.find((existingFile: any) => {
      for (let i = 0; i < files.length; i++) {
        if (existingFile.name === files.item(i)?.name) {
          return true;
        }
      }
      return false;
    });
  }

  private addFilesToProject(files: FileList): void {
    for (let i = 0; i < files.length; i++) {
      this.projectFiles.push(files.item(i));
    }
  }

  addDocumentsToList(): void{
    this.docTitleRequired = false;
    this.docAdded = true;
    const files = this.projectFiles;       
    files.forEach((d:any, i:number)=>{
      const documentExists = this.templateDocuments.find((doc: any) => doc.name === d.name);
      if(documentExists){
        return;
      }
      this.templateDocuments.push({file: d, docTitle: ``, name: d.name});
    })
  }

  onUploadDocumentsNext() {
    if (this.projectFiles && this.projectFiles.length) {
      if(!this.templateName){
        this.toastService.showError("Template name is missing");
        return;
      }
      for(const document of this.templateDocuments){
        if(!document.docTitle){
          this.toastService.showError("Doc title missing");
          return;
        }
      }
      if(this.selectedDocSignTemplate){
        this.permanentAddconfirmVisible = true;
      } else {
        this.onDocuSignEnableNext('');
        
      }
      this.hideDialog();
    }
  }

  async uploadResumeDocument($event: Event) {
    const input = $event.target as HTMLInputElement;
    const files = input.files;
    if (files && files.length) {
      if(files[0].type === 'application/pdf') { 
        const isValid = await this.helperService.validatePdf(files[0]);
        if (!isValid) {
          this.toastService.showError("Corrupted or invalid PDF file.");
          return;
        }
      }
      this.projectResumeFile = files[0];
      this.fileName = files[0].name;
      this.projectFiles = files;
      this.showResumeDocUploadError = '';
      input.value = '';
    }
  }

  saveResumeDocumentAndNext(): any{
    const isReadOnlyLinkFlow = !!this.firm?.documentsReadOnly && this.readOnlyDocumentType === 'LINK';

    if(!isReadOnlyLinkFlow && !this.projectResumeFile){
      this.showResumeDocUploadError = 'No file selected.';
      return;
    }
    if(this.resumeDocumentForm && !this.resumeDocumentForm.valid){
      this.resumeForm['docTitle'].markAsDirty()
      return;
    }

    if (isReadOnlyLinkFlow) {
      const linkUrl = this.getTrimmedUrl();
      if (!linkUrl) {
        this.toastService.showError('URL is required');
        return;
      }
      if (!this.isValidHttpUrl(linkUrl)) {
        this.toastService.showError('Please provide a valid URL starting with http:// or https://');
        return;
      }
    }

    const fileOpts: any = {
      projectId: this.projectId,
      documentType: 'BLANK_FORM'
    };

    if (isReadOnlyLinkFlow) {
      fileOpts.url = this.getTrimmedUrl();
    }

    if (this.resumeDocumentForm.value.instructions) {
        fileOpts.userTitle = this.resumeDocumentForm.value.instructions;
    }
    if (this.resumeDocumentForm.value.docTitle) {
        fileOpts.docTitle = this.resumeDocumentForm.value.docTitle;
    }
    this.isSaveTemplateInProgress = true;
    this.documentService.uploadResumeDocument(this.projectId || '', this.projectResumeFile, fileOpts).subscribe({
      next: (response) => {
        
        this.updateProjectState.emit();
        this.hideResumeDialog();
        this.isSaveTemplateInProgress = false;
        this.projectResumeFile = null;
      },
      error: (error: any) => {
        let errorMessage = error.data ? error.data.message: '';
        if (error.statusText && error.statusText === 'Request Entity Too Large') {
            errorMessage = "File is too large";
        }
        this.toastService.showError(errorMessage);
        this.isSaveTemplateInProgress = false;
        this.projectResumeFile = null;
      },
    });
  }

  async updateResumeDocumentAndNext(): Promise<void>{
    if(this.resumeDocumentForm && !this.resumeDocumentForm.valid){
      this.resumeForm['docTitle'].markAsDirty();
      return;
    }

    const isReadOnlyLinkFlow = !!this.firm?.documentsReadOnly && this.readOnlyDocumentType === 'LINK';

    if (isReadOnlyLinkFlow) {
      const linkUrl = this.getTrimmedUrl();
      if (!linkUrl) {
        this.toastService.showError('URL is required');
        return;
      }
      if (!this.isValidHttpUrl(linkUrl)) {
        this.toastService.showError('Please provide a valid URL starting with http:// or https://');
        return;
      }
    }

    const docInfo: {userTitle: string, docTitle: string, url?: string} = {
      userTitle: '',
      docTitle: ''

    }

    if (this.resumeDocumentForm.value.instructions) {
      docInfo.userTitle = this.resumeDocumentForm.value.instructions;
    }

    if (this.resumeDocumentForm.value.docTitle) {
      docInfo.docTitle = this.resumeDocumentForm.value.docTitle;
    }

    if (isReadOnlyLinkFlow) {
      docInfo.url = this.getTrimmedUrl();
    }

    this.isSaveTemplateInProgress = true;

    await this.documentService.updateResumeDocument(this.selectedResumeDocument?._id || '', docInfo);
    this.isSaveTemplateInProgress = false;
    this.selectedResumeDocument = null;
    this.visibleResumeDialog = false;
    this.resumeDocumentForm.reset();
    this.updateProjectState.emit();
  }

  

  updateInExistingTemplate(response: any){

    let document = response.file[0].file;
    this.pdfDocuments = this.pdfDocuments.concat(response.file);

    let fileOptions = this.pdfDocuments.map((doc: any) => {
      return {
        projectId: this.projectId,
        name: document.name,
        docTitle: doc.docTitle
      };
    });

    this.hideDialog();
    this.isSaveTemplateInProgress = true;
    this.projectService.updateDocuSignTemplate(document, fileOptions, this.selectedDocSignTemplate.name, this.selectedDocSignTemplate.templateId, this.selectedDocSignTemplate.documents.length)
      .subscribe(
        {
          next: (res) => {
            this.getDocuSignTemplates();
            this.addToTemplate = true;
            this.toastService.showSuccess("Document added successfully.");
            this.projectFiles = [];
            this.docTitle = '';
            this.templateDocuments = [];
            this.pdfDocuments = [];
            this.hideDialog();
            this.selectTemplateType = 'Existing';
            this.reloadTableData();
            this.selectedDocSignTemplate = null;
            const documentName = res.documents && res.documents.length ? res.documents[res.documents.length - 1].name || '' : '';
            this.editTemplateDocumentView(res.templateId, res.documents.length, documentName, res);
            this.isSaveTemplateInProgress = false;
          },
          error: (error) => {
            this.onFailureUpsertingTemplate(error);
            this.isSaveTemplateInProgress = false;
          }
        }
      );
  }

  getTemplateFieldsOrDefault(templateFileds: any) {
    if(Object.keys(templateFileds).length === 0) {
      let obj = {
        signHere:[],
        dateSigned:[],
        textTab_fullName:[],
        textTab_email:[],
        textTab_custom:[],
        checkBoxTab:[],
        initialHere:[],
        radioTab:[],
        signerAttachment:[]
      };
      return obj;
    }
    return templateFileds;
  }


  isGreaterThanAllowedSize(file: any){
    return (file.size > constants.documentUploadSizeInMbs * 1000000);
  }

  addNewTemplate(): void{
    this.selectedDocSignTemplate = null
    this.showDialog();
  }

  showDialog() {
    if(this.selectedDocSignTemplate){
      this.templateName = this.selectedDocSignTemplate.name;
    }
    this.selectTemplateType = 'New';
  }

  back(){
    this.selectTemplateType = 'Existing';
    this.projectFiles = [];
    this.docTitle = '';
    this.templateName = '';
    this.templateDocuments = [];
  }

  hideDialog(){
    this.visible = false;
  }

  deleteDocument($event: Event, documentId: string) {
    $event.stopPropagation();
    this.confirmationDialogService.confirm({
      target: $event.target as EventTarget,
      message: 'Are you sure you want to delete this document?',
      header: 'Confirmation',
      icon: 'pi pi-exclamation-triangle',
      acceptIcon: "none",
      rejectIcon: "none",
      rejectButtonStyleClass: "p-button-text",
      accept: () => {
        this.documentService.deleteDocument(documentId).subscribe((d: any) => {
          this.toastService.showSuccess('Document deleted successfully');
          this.updateProject();
        })
      }
    });
  }

  showResumeDialog() {
    this.resumeDocumentForm.controls['docTitle'].setValue('');
    this.resumeDocumentForm.controls['instructions'].setValue('');
    this.resumeDocumentForm.get('url')?.setValue('');
    this.projectResumeFile = null;
    this.showResumeDocUploadError = '';
    this.readOnlyDocumentType = 'DOCUMENT';
    this.isEditResumeDocument = false;
    this.visibleResumeDialog = true;
  }

  showEditResumeDialog(documents: Documents){
    this.selectedResumeDocument = documents;
    this.resumeDocumentForm.controls['docTitle'].setValue(this.selectedResumeDocument.docTitle);
    this.resumeDocumentForm.controls['instructions'].setValue(this.selectedResumeDocument.userTitle || '');
    this.resumeDocumentForm.get('url')?.setValue(this.selectedResumeDocument.url || '');
    this.readOnlyDocumentType = this.selectedResumeDocument.url ? 'LINK' : 'DOCUMENT';
    this.isEditResumeDocument = true;
    this.visibleResumeDialog = true;
  }

  hideResumeDialog(){
    this.visibleResumeDialog = false;
    this.resumeDocumentForm.reset();
    this.fileName = 'No file selected';
    this.readOnlyDocumentType = 'DOCUMENT';
  }

  async saveAndLogout(): Promise<void>{
    const currentProjectSteo = Number(this.project?.step || 0);
    if (currentProjectSteo < 5) {
      const pId = this.projectId || '';
      this.projectNavigation.setCurrentStep(4);
      await this.projectService.updateProjectStep(pId, 4);
      this.goToProjectListing();
    } else {
        this.projectNavigation.setCurrentStep(currentProjectSteo);
        this.goToProjectListing();
    }
  }

  goToProjectListing(): void {
    switch (this.project?.status) {
        case constants.projectStatus.created:
            this.router.navigate([`/client/project/unpublished`]);
            break;
        case constants.projectStatus.published:
            this.router.navigate([`/client/project/published`]);
            break;
        case constants.projectStatus.started:
            this.router.navigate([`/client/project/started`]);
            break;
        case constants.projectStatus.archived:
            this.router.navigate([`/client/project/archived`]);
            break;
        default:
            this.router.navigate([`/client/overview`]);
    }
  }

  onRowExpand(event: TableRowExpandEvent) {}
  onRowCollapse(event: TableRowCollapseEvent) {}
}
